import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import RiskLevel, { riskLevelType } from '../../components/ui/Risk';
import { Card, Divider, makeStyles } from '@material-ui/core';

enum reportType {
  Critcal = 'Critical',
  High = 'High',
  Medium = 'Medium',
  Low = 'Low',
  Info = 'Info',
}

const severityList = ['High', 'Medium', 'Low', 'Info', 'Critical'] as const;

export interface checmarxProps {
  report: {
    totalCounters: {
      severityCounters: {
        severity: (typeof severityList)[number];
        counter: number;
      }[];
    };
    riskLevel: string;
    lastScanDate: string;
    enginesData: {
      lastScanId: string;
    }[];
  };
  moreDetailsUrl: string;
  title: string;
}

const useStyles = makeStyles({
  header: {
    marginLeft: '20px',
  },
  graph: {
    display: 'flex',
    gap: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  vulnerabilityScore: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  scanDetails:{
    textAlign:'center',
    marginTop:'20px'
  }
});

const VulnerabilityChart = ({
  report,
  moreDetailsUrl,
  title,
}: checmarxProps) => {
  const classes = useStyles();
  const chartRef = useRef<SVGSVGElement | null>(null);

  const getCount = (sev: string) =>
    report?.totalCounters?.severityCounters.find(
      x => x.severity.toLowerCase() === sev.toLowerCase(),
    )?.counter ?? 0;

  const critical = getCount(reportType.Critcal);
  const high = getCount(reportType.High);
  const medium = getCount(reportType.Medium);
  const low = getCount(reportType.Low);
  const info = getCount(reportType.Info);
  useEffect(() => {
    if (!chartRef.current) return;

    const svg = d3.select(chartRef.current);
    svg.selectAll('*').remove(); // Clear previous render

    // Chart dimensions
    const width = 500,
      height = 400;
    const margin = { top: 20, right: 30, bottom: 50, left: 50 };

    // Prepare data
    const vulnerabilities = [
      { name: 'Critical', value: critical, color: '#BB2A31' }, // Red
      { name: 'High', value: high, color: '#F1605D' }, // Orange
      { name: 'Medium', value: medium, color: '#F9AE4D' }, // Yellow
      { name: 'Low', value: low, color: '#BDBDBD' }, // Green
      { name: 'Info', value: info, color: '#A9A9A9' }, // Green
    ];

    // Scales
    const xScale = d3
      .scaleBand()
      .domain(vulnerabilities.map(d => d.name))
      .range([margin.left, width - margin.right])
      .padding(0.4);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(vulnerabilities, d => d.value) || 1])
      .nice()
      .range([height - margin.bottom, margin.top]);

    // Draw bars
    const bars = svg
      .append('g')
      .selectAll('rect')
      .data(vulnerabilities)
      .enter()
      .append('rect')
      .attr('x', d => xScale(d.name)!)
      .attr('y', d => yScale(d.value))
      .attr('height', d => height - margin.bottom - yScale(d.value))
      .attr('width', xScale.bandwidth())
      .attr('fill', d => d.color)
      .attr('rx', 5);

    // Display value on hover
    const tooltip = svg
      .append('text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('opacity', 0)
      .style('font-size', '14px')
      .style('fill', 'white');

    // Hover effect
    bars
      .on('mouseover', function (event, d) {
        // Get the x and y positions to display the tooltip above the bar
        const [x, y] = d3.pointer(event);

        // Display the value on top of the bar
        tooltip
          .transition()
          .duration(200)
          .attr('x', x)
          .attr('y', y - 10)
          .attr('opacity', 1)
          .text(d.value);
      })
      .on('mouseout', function () {
        // Hide the tooltip when mouse leaves the bar
        tooltip.transition().duration(200).attr('opacity', 0);
      });

    // X-Axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(xScale));

    // Y-Axis
    svg
      .append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(yScale));
  }, [report]);

  const total = info + low + medium + high + critical;

  return (
    <>
      <Card>
        <h2 className={classes.header}>{title}</h2>
        <Divider />
        <div className={classes.scanDetails}>
          Last Scan Id: <b>{report?.enginesData[0]?.lastScanId} </b>
        </div>
        <div style={{ textAlign: 'center' }}>
          Last Scan Date: <b>{report?.lastScanDate?.split('T')[0]}</b>
        </div>

        <section style={{ textAlign: 'center' }}>
          <a
            href={moreDetailsUrl?.concat('/')}
            style={{ color: 'blue' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            More details
          </a>
        </section>

        <br />
        <div className={classes.graph}>
          <svg ref={chartRef} width={600} height={500}></svg>
          <section className={classes.vulnerabilityScore}>
            <b>Vulnerability score:</b>
            <b>Critical : {critical}</b>
            <b>High : {high}</b>
            <b>Medium : {medium}</b>
            <b>Low : {low}</b>
            <b>Info : {info}</b>
            <br />
            <b>Total : {total} </b>

            <br />

            <RiskLevel risk={report?.riskLevel as riskLevelType} />

            <br />
          </section>
        </div>
      </Card>
    </>
  );
};

export default VulnerabilityChart;
